import { Injectable } from '@angular/core';
import { AsyncSubject, BehaviorSubject, Observable, filter, finalize, first, map, partition, repeat, takeUntil } from 'rxjs';

import { NeverError } from '../models/error.model';
import {
  Project,
  ProjectCreateParams,
  ProjectQueryParams,
  ProjectUpdateParams,
  Projects,
  ReportCreateParams,
} from '../models/project.model';
import { Unit, UnitCreateParams, UnitUpdateParams } from '../models/unit.model';
import { UserTimezone } from '../models/user.model';
import { DistinctSubject, Period, PeriodSubject, recursiveQuery } from '../models/utility.model';
import { WebSocketSyncData } from '../models/web-socket.model';
import { AuthUsecase } from '../usecases/auth.usecase';
import { ProjectGateway } from '../usecases/project.gateway';
import { ProjectUsecase } from '../usecases/project.usecase';
import { UnitGateway } from '../usecases/unit.gateway';
import { WebSocketUsecase } from '../usecases/web-socket.usecase';

@Injectable()
export class ProjectInteractor extends ProjectUsecase {
  get projects$(): Observable<Projects> {
    return this._projects;
  }
  get period$(): Observable<Period> {
    return this._period;
  }
  get unitUpdateNotice$(): Observable<void> {
    return this._unitUpdateNotice;
  }

  private readonly _projects = new DistinctSubject<Projects>(new Projects());
  private readonly _period = new PeriodSubject({ from: -1, to: -1 });
  private readonly _unitUpdateNotice = new BehaviorSubject<void>(void 0);

  constructor(
    private _authUsecase: AuthUsecase,
    private _webSocketUsecase: WebSocketUsecase,
    private _projectGateway: ProjectGateway,
    private _unitGateway: UnitGateway,
  ) {
    super();
    this._authUsecase.authState$.pipe(first(({ status }) => status === 'signedIn')).subscribe(() => {
      this._period.now();
    });

    const [open$, close$] = partition(this._webSocketUsecase.isOpen$, isOpen => isOpen);
    this.period$
      .pipe(
        filter(({ from, to }) => from >= 0 && to >= 0),
        takeUntil(close$),
        finalize(() => this._projects.next(new Projects())),
        repeat({ delay: () => open$ }),
      )
      .subscribe(({ from, to }) => this.listProjects(from, to));

    this._webSocketUsecase.isOpen$.subscribe(isOpen => (isOpen ? this.onSignIn() : this.onSignOut()));
    this._webSocketUsecase.message$
      .pipe(
        filter(message => message.action === 'sync' && message.data?.source === 'project'),
        map(({ data }) => data as WebSocketSyncData<Project>),
      )
      .subscribe(data => {
        switch (data.reason) {
          case 'create':
          case 'update':
            this._projects.next(this._projects.value.set(data.payload as Project));
            break;
          case 'delete': {
            this._projects.next(this._projects.value.delete((data.payload as Project).projectId));
            break;
          }
          default:
            throw new NeverError(data.reason);
        }
      });
    this._webSocketUsecase.message$
      .pipe(
        filter(message => message.action === 'sync' && message.data?.source === 'unit'),
        map(({ data }) => data as WebSocketSyncData<Unit>),
      )
      .subscribe(data => {
        const unit = data.payload as Unit;
        switch (data.reason) {
          case 'create':
            this.onCreateUnit(unit);
            break;
          case 'update':
            this.onUpdateUnit(unit);
            break;
          case 'delete':
            this.onDeleteUnit(unit.projectId, unit.unitId);
            break;
          default:
            throw new NeverError(data.reason);
        }
      });
  }

  changeTimezone(timezone: UserTimezone): void {
    this._period.tz(timezone);
  }

  changePeriod(period: Period): void {
    this._period.next(period);
  }

  createProject(params: ProjectCreateParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._projectGateway.createProject(params).subscribe({
      next: createdProject => this._projects.next(this._projects.value.set(createdProject)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  updateProject(projectId: string, params: ProjectUpdateParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._projectGateway.updateProject(projectId, params).subscribe({
      next: updatedProject => this._projects.next(this._projects.value.set(updatedProject)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  deleteProject(projectId: string): Observable<never> {
    const result = new AsyncSubject<never>();
    this._projectGateway.deleteProject(projectId).subscribe({
      next: () => this._projects.next(this._projects.value.delete(projectId)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  getConsentForm(projectId: string, consentId: string): Observable<string> {
    const result = new AsyncSubject<string>();
    this._projectGateway.getConsentForm(projectId, consentId).subscribe({
      next: ({ url }) => result.next(url),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  createReport(projectId: string, params: ReportCreateParams): Observable<never> {
    const result = new AsyncSubject<never>();
    this._projectGateway.createReport(projectId, params).subscribe({
      next: createdProject => this._projects.next(this._projects.value.set(createdProject)),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  createUnit(projectId: string, params: UnitCreateParams): Observable<string> {
    const result = new AsyncSubject<string>();
    this._unitGateway.createUnit(projectId, params).subscribe({
      next: createdUnit => {
        result.next(createdUnit.unitId);
        this.onCreateUnit(createdUnit);
      },
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  updateUnit(projectId: string, unitId: string, params: UnitUpdateParams): Observable<Unit> {
    const result = new AsyncSubject<Unit>();
    this._unitGateway.updateUnit(projectId, unitId, params).subscribe({
      next: updatedUnit => {
        this.onUpdateUnit(updatedUnit);
        result.next(updatedUnit);
      },
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  updateUnitAssign(projectId: string, unitId: string): Observable<never> {
    const result = new AsyncSubject<never>();
    this._unitGateway.updateUnitAssign(projectId, unitId).subscribe({
      next: updatedUnit => this.onUpdateUnit(updatedUnit),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  deleteUnit(projectId: string, unitId: string): Observable<never> {
    const result = new AsyncSubject<never>();
    this._unitGateway.deleteUnit(projectId, unitId).subscribe({
      next: () => this.onDeleteUnit(projectId, unitId),
      error: result.error.bind(result),
      complete: result.complete.bind(result),
    });
    return result.asObservable();
  }

  private onCreateUnit(createdUnit: Unit): void {
    const project = this._projects.value.get(createdUnit.projectId);
    if (project && !project.units?.find(({ unitId }) => unitId === createdUnit.unitId)) {
      project.units = [...(project.units || []), createdUnit];
      this._projects.next(this._projects.value.set(project));
      this._unitUpdateNotice.next();
    }
  }

  private onUpdateUnit(updatedUnit: Unit): void {
    const project = this._projects.value.get(updatedUnit.projectId);
    if (project && project.units?.find(({ unitId, version }) => unitId === updatedUnit.unitId && version < updatedUnit.version)) {
      project.units = project.units?.reduce((acc, cur) => {
        const unit = cur.unitId === updatedUnit.unitId ? updatedUnit : cur;
        return [...acc, unit];
      }, [] as Unit[]);
      this._projects.next(this._projects.value.set(project));
      this._unitUpdateNotice.next();
    }
  }

  private onDeleteUnit(projectId: string, unitId: string): void {
    const project = this._projects.value.get(projectId);
    if (project) {
      project.units = project.units?.reduce((acc, cur) => {
        return cur.unitId === unitId ? acc : [...acc, cur];
      }, [] as Unit[]);
      this._projects.next(this._projects.value.set(project));
      this._unitUpdateNotice.next();
    }
  }

  private listProjects(from: number, to: number): void {
    const queryParams = {
      scheduledFrom: from.toString(),
      scheduledTo: to.toString(),
    } as ProjectQueryParams;
    recursiveQuery(params => this._projectGateway.listProjects(params), queryParams).subscribe(projects => {
      this._projects.next(new Projects(projects));
    });
  }

  private onSignIn(): void {
    const { from, to } = this._period.value;
    this.listProjects(from, to);
  }

  private onSignOut(): void {
    this._projects.next(new Projects());
  }
}
